import { NoData } from '@css-xp/ppc-common';
import { SkeletonLoader, SkeletonLoaderWrapper } from '@css-xp/skeleton-loader';
import isEmpty from 'lodash/isEmpty';
import React, { useState, useEffect } from 'react';
import { PricingPlanType } from '@atlassian/cssxp-typescommon';
import { PlanTypeCell } from '@css-xp/ppc-purchase-experience';
import { useStore } from '../../../../store';
import { State } from '../../../../store/types';
import { getProductIdsOfferingId } from '../../../../store/utils';
import { CartProductSchema, CartLinkedEntitlementsSchema, OperationType } from '../../../../types';
import { ProductIdPlanOptionsMap } from '../types';
import { getCollectionToInstanceOfferingMap } from '../../../../store/utils/collections-offerings-utils';

export const LineItemPlanCell = ({
  productIdPlanOptionsMap,
  lineItem,
  offeringsAndPricingPlans,
  setSelectedPricingPlanId,
  isTransferStandardPaidProduct,
  selectedOrDefaultPriceEligibility,
  hasLinkedAddOns,
  hasSandboxesLinked,
  isOnUpgradeTrial,
  renderReadOnlyPlan,
  isLinkedEntitlement,
  setHasErrorFetchingOfferings,
  isMultiCurrencyEnabled,
  isAddOnsSearchEnabled,
  orgProductInstancesMap,
  isTWCPhase2Enabled,
}: {
  productIdPlanOptionsMap: ProductIdPlanOptionsMap;
  lineItem: any;
  offeringsAndPricingPlans?: State['offeringsAndpricingPlans'];
  isTransferStandardPaidProduct?: boolean;
  setSelectedPricingPlanId: (
    updatedPricingPlanId: string,
    updatedOfferingId: string,
    productId: CartProductSchema['productId']
  ) => void;
  selectedOrDefaultPriceEligibility?: `${PricingPlanType}`;
  hasLinkedAddOns?: boolean;
  hasSandboxesLinked?: boolean;
  isOnUpgradeTrial?: boolean;
  renderReadOnlyPlan: (
    offeringName: string,
    isNonCommercialPricingEnabled?: boolean,
    isLinkedEntitlement?: boolean,
    priceEligibility?: `${PricingPlanType}`
  ) => JSX.Element;
  isLinkedEntitlement?: boolean;
  setHasErrorFetchingOfferings?: (hasError: boolean) => void;
  isMultiCurrencyEnabled?: boolean;
  isAddOnsSearchEnabled?: boolean;
  orgProductInstancesMap?: Record<string, CartProductSchema[]>;
  isTWCPhase2Enabled?: boolean;
}) => {
  const [, actions] = useStore();
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const fetchOfferingOptions = async () => {
      setLoading(true);
      const offeringsAndPricingPlansDetails = await getProductIdsOfferingId({
        existingOfferingsAndPricingPlans: offeringsAndPricingPlans,
        productIds: [lineItem?.productId],
        isMultiCurrencyEnabled: isMultiCurrencyEnabled,
        isAddOnsSearchEnabled: isAddOnsSearchEnabled,
      });

      if (isEmpty(offeringsAndPricingPlansDetails)) {
        setHasError(true);
        setHasErrorFetchingOfferings && setHasErrorFetchingOfferings(true);
      } else {
        actions.updateOfferingAndPricingPlans(offeringsAndPricingPlansDetails);
      }
      setLoading(false);
    };
    const fetchCollectionToInstanceOfferingMap = async () => {
      setLoading(true);
      const response = await getCollectionToInstanceOfferingMap();
      if (isEmpty(response)) {
        setHasError(true);
        setHasErrorFetchingOfferings && setHasErrorFetchingOfferings(true);
      } else {
        actions.updateCollectionToInstanceOfferingMap(response);
      }
      setLoading(false);
    };
    if (lineItem && productIdPlanOptionsMap[lineItem.id]?.length === 0) {
      fetchOfferingOptions();
    }
    if (
      isTWCPhase2Enabled &&
      lineItem &&
      lineItem.isOrgProduct &&
      orgProductInstancesMap &&
      orgProductInstancesMap[lineItem.id] &&
      orgProductInstancesMap[lineItem.id].length > 0
    ) {
      fetchCollectionToInstanceOfferingMap();
    }
  }, []);

  if (loading) {
    return (
      <SkeletonLoaderWrapper>
        <SkeletonLoader />
      </SkeletonLoaderWrapper>
    );
  }

  if (hasError || productIdPlanOptionsMap[lineItem?.id]?.length === 1) {
    return (
      renderReadOnlyPlan(lineItem?.offeringName, true, isLinkedEntitlement, selectedOrDefaultPriceEligibility) ?? (
        <NoData />
      )
    );
  }

  const isTransferStandardPaidApp = (lineItem: CartLinkedEntitlementsSchema) => {
    return (
      lineItem.operationType === OperationType.TRANSFER &&
      productIdPlanOptionsMap[lineItem.id]?.find((option) => option.offeringId === lineItem?.offeringId)
        ?.offeringName === 'Standard' &&
      lineItem.inTrial === false
    );
  };

  return (
    <PlanTypeCell
      offeringId={lineItem.offeringId}
      offeringName={lineItem?.offeringName}
      offeringLevel={lineItem?.offeringLevel}
      setSelectedPricingPlanId={(updatedPricingPlanId, updatedOfferingId) => {
        setSelectedPricingPlanId(updatedPricingPlanId, updatedOfferingId, lineItem.id);
      }}
      offeringPricingPlanOptions={productIdPlanOptionsMap[lineItem?.id]}
      isTransferStandardPaid={isTransferStandardPaidProduct || isTransferStandardPaidApp(lineItem)}
      hasSandboxesLinked={hasSandboxesLinked}
      selectedOrDefaultPriceEligibility={selectedOrDefaultPriceEligibility}
      enableDesignIteration1Changes={true}
      isOnUpgradeTrial={isOnUpgradeTrial}
      hasLinkedAddOns={hasLinkedAddOns}
      productId={lineItem.productId}
      productName={lineItem.name}
    />
  );
};
